// E37-t6 — THE FILE-TREE BROWSER.   route: /:org/:kb/files
//
// The canonical core, read as a tree: the corpus snapshot's manifest drawn as
// collapsible directories of DOCUMENTS, and one document at a time rendered as
// markdown beside it. Read-only by construction — this page carries no verb;
// changing the corpus remains a push or a command (one-way document flow).
//
// Contract: e35-37-design.md §5 E37-t6 — "KbFiles.tsx (tree from manifest,
//   …, 500 KB text cap, header names the checkout commit, read-only by
//   construction — writes remain commands/pushes)"; L26 — the browser reads
//   the R2 corpus snapshot (git-host/src/corpus-read.ts is the door).
//
// WHAT THE TREE SHOWS, and what it deliberately does not: DOCUMENTS (`.md`)
// and the directories that hold them. Sidecars (`*.records.yaml`), local
// mirrors (`*.records.local.yaml`), formats, dashboards and every other
// non-document file are HIDDEN — they are data, not reading; the surfaces
// that render that data (dashboards, record views) already exist and a raw
// YAML pane here would be a second, worse one. The DOOR still answers the
// whole manifest: hiding is this page's presentation, not the wire's.
//
// DEVIATION FROM THE DESIGN LINE, on the record: §5 E37-t6 said "sidecars
// beside their .md twin"; the stage ticket overrides it by name ("sidecars
// and local mirrors hidden — they are data, not reading") and this page
// follows the ticket.
//
// RENDERING: web/src/lib/markdown.ts — the design's named fallback while the
// pinned engine exports no renderMarkdownBody (that file's header argues it).
// Internal `.md` links in a rendered body navigate INSIDE the tree when the
// manifest carries the target, so reading follows references without leaving
// the checkout the header names.
//
// DEGRADE, per panel (KbWorkspace's doctrine): a refused manifest costs the
// page its tree and shows the transport's own sentence; a refused document
// read costs the READER PANE alone — the tree stays, another document is one
// click away. A document over the 500 KB cap is never fetched: the manifest
// already states its size, and the cap sentence says the number.
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Stack } from '@astryxdesign/core/Stack';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';

import type { CorpusFile, CorpusTree, FilesClient } from '../api/files';
import { Eyebrow } from '../components/Eyebrow';
import { messageOf } from '../format/errors';
import { humanBytes, humanCount, relShort } from '../format/units';
import { renderMarkdownDoc } from '../lib/markdown';

import './kb-files.css';

/** The reading cap the design names: past it the page states the size and
 *  declines, rather than parsing half a megabyte of text into a DOM. */
export const RENDER_CAP_BYTES = 500 * 1024;

export interface KbFilesProps {
  org: string;
  kb: string;
  files: FilesClient;
  /** `?doc=` — the document a shared link opened on; the page keeps the
   *  query current as the selection moves, so every view is linkable. */
  initialDoc?: string | null;
}

// ---------------------------------------------------------------------------
// the tree: manifest paths → nested directories of documents
// ---------------------------------------------------------------------------

export interface DocNode {
  name: string;
  path: string;
  size: number;
}

export interface DirNode {
  name: string;
  path: string;
  dirs: DirNode[];
  docs: DocNode[];
}

/** Documents only — the hiding the header argues. */
export function documentsOf(files: CorpusFile[]): CorpusFile[] {
  return files.filter(
    (f) =>
      f.path.toLowerCase().endsWith('.md') &&
      // belt beside the suspenders: a sidecar or local mirror is never a
      // document even if a future suffix ends in .md-something
      !/\.records(\.local)?\.yaml$/i.test(f.path),
  );
}

/** The manifest's flat paths as a directory tree, children sorted by name. */
export function buildTree(files: CorpusFile[]): DirNode {
  const root: DirNode = { name: '', path: '', dirs: [], docs: [] };
  const dirAt = new Map<string, DirNode>([['', root]]);
  for (const file of documentsOf(files)) {
    const segments = file.path.split('/');
    const name = segments.pop() as string;
    let parent = root;
    let prefix = '';
    for (const segment of segments) {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      let dir = dirAt.get(prefix);
      if (!dir) {
        dir = { name: segment, path: prefix, dirs: [], docs: [] };
        dirAt.set(prefix, dir);
        parent.dirs.push(dir);
      }
      parent = dir;
    }
    parent.docs.push({ name, path: file.path, size: file.size });
  }
  const sort = (dir: DirNode) => {
    dir.dirs.sort((a, b) => a.name.localeCompare(b.name));
    dir.docs.sort((a, b) => a.name.localeCompare(b.name));
    dir.dirs.forEach(sort);
  };
  sort(root);
  return root;
}

/** Resolve a rendered body's relative `.md` link against the open document. */
export function resolveDocLink(fromDoc: string, href: string): string {
  const base = fromDoc.split('/').slice(0, -1);
  const out = href.startsWith('/') ? [] : [...base];
  const raw = href.replace(/^\//, '');
  for (const segment of raw.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      out.pop();
      continue;
    }
    out.push(segment);
  }
  return out.join('/');
}

// ---------------------------------------------------------------------------
// the page
// ---------------------------------------------------------------------------

type TreeRead =
  | { state: 'reading' }
  | { state: 'ready'; tree: CorpusTree }
  | { state: 'refused'; reason: string };

type DocRead =
  | { state: 'idle' }
  | { state: 'reading'; path: string }
  | { state: 'ready'; path: string; html: string; frontmatter: string[] }
  | { state: 'toolarge'; path: string; size: number }
  | { state: 'refused'; path: string; reason: string };

const shortOid = (oid: string): string => oid.slice(0, 7);

export function KbFiles({ org, kb, files, initialDoc = null }: KbFilesProps) {
  const [read, setRead] = useState<TreeRead>({ state: 'reading' });
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState<string | null>(initialDoc || null);
  const [doc, setDoc] = useState<DocRead>({ state: 'idle' });

  useEffect(() => {
    let live = true;
    setRead({ state: 'reading' });
    void files
      .loadTree(org, kb)
      .then((tree) => {
        if (live) setRead({ state: 'ready', tree });
      })
      .catch((error) => {
        if (live) setRead({ state: 'refused', reason: messageOf(error) });
      });
    return () => {
      live = false;
    };
  }, [org, kb, files, attempt]);

  const manifest = read.state === 'ready' ? read.tree : null;
  const documents = useMemo(
    () => (manifest ? documentsOf(manifest.files) : []),
    [manifest],
  );
  const tree = useMemo(() => buildTree(manifest?.files ?? []), [manifest]);

  // the selection is LINKABLE: the query string follows it (replaceState, not
  // navigate — the page does not remount over its own click)
  const select = (path: string | null) => {
    setSelected(path);
    const url = new URL(window.location.href);
    if (path) url.searchParams.set('doc', path);
    else url.searchParams.delete('doc');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  useEffect(() => {
    if (!manifest || !selected) {
      setDoc({ state: 'idle' });
      return;
    }
    const entry = documents.find((f) => f.path === selected);
    if (!entry) {
      setDoc({
        state: 'refused',
        path: selected,
        reason: `the snapshot of ${shortOid(manifest.commit)} carries no document at "${selected}"`,
      });
      return;
    }
    // the cap, decided from the manifest's own size — the fetch never starts
    if (entry.size > RENDER_CAP_BYTES) {
      setDoc({ state: 'toolarge', path: selected, size: entry.size });
      return;
    }
    let live = true;
    setDoc({ state: 'reading', path: selected });
    void files
      .loadFile(org, kb, selected)
      .then((text) => {
        if (!live) return;
        const rendered = renderMarkdownDoc(text);
        setDoc({ state: 'ready', path: selected, ...rendered });
      })
      .catch((error) => {
        if (live) setDoc({ state: 'refused', path: selected, reason: messageOf(error) });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, manifest, org, kb, files]);

  /** rendered-body clicks: an internal `.md` link stays inside the tree */
  const onBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a[data-doc]');
    if (!anchor) return;
    event.preventDefault();
    const href = anchor.getAttribute('data-doc') ?? '';
    if (!href || !selected) return;
    const resolved = resolveDocLink(selected, href);
    const target = documents.find((f) => f.path === resolved || f.path === href);
    if (target) select(target.path);
  };

  const renderDir = (dir: DirNode) => (
    <details key={dir.path} open className="dj-files-dir">
      <summary>
        <Text type="label" weight="semibold">
          {dir.name}
        </Text>
      </summary>
      <div className="dj-files-children">
        {dir.dirs.map((child) => renderDir(child))}
        {dir.docs.map((docNode) => renderDoc(docNode))}
      </div>
    </details>
  );

  const renderDoc = (docNode: DocNode) => (
    <button
      key={docNode.path}
      type="button"
      className="dj-files-doc"
      aria-current={selected === docNode.path ? 'true' : undefined}
      onClick={() => select(docNode.path)}
    >
      {docNode.name}
    </button>
  );

  return (
    <div data-page="kb-files" className="dj-files">
      <header className="dj-files-head">
        <Stack direction="horizontal" gap={4} vAlign="center" hAlign="between" wrap="wrap">
          <Stack direction="vertical" gap={1}>
            <Heading level={1} type="display-3">
              Files
            </Heading>
            {/* the header names the CHECKOUT: what commit these bytes are */}
            {manifest ? (
              <Text type="code" color="secondary">
                {humanCount(documents.length)}{' '}
                {documents.length === 1 ? 'document' : 'documents'}
                <span className="dj-sep" aria-hidden>
                  {' · '}
                </span>
                checkout {shortOid(manifest.commit)}
                {manifest.updatedAt ? (
                  <>
                    <span className="dj-sep" aria-hidden>
                      {' · '}
                    </span>
                    {relShort(manifest.updatedAt)}
                  </>
                ) : null}
              </Text>
            ) : null}
          </Stack>
          <Button label="Workspace" variant="ghost" size="sm" href={`/${org}/${kb}`} />
        </Stack>
      </header>

      {read.state === 'reading' ? (
        <div className="dj-files-body" aria-hidden="true">
          <Skeleton width="38%" height="12px" index={0} />
          <Skeleton width="52%" height="12px" index={1} />
          <Skeleton width="44%" height="12px" index={2} />
        </div>
      ) : read.state === 'refused' ? (
        <div className="dj-files-body">
          <Stack direction="horizontal" gap={2} vAlign="center">
            <StatusDot variant="error" label="File tree refused" />
            <Text type="label" weight="semibold">
              File tree refused
            </Text>
          </Stack>
          <Text type="code" color="secondary">
            {read.reason}
          </Text>
          <Stack direction="horizontal" gap={2}>
            <Button
              label="Retry"
              variant="secondary"
              size="sm"
              onClick={() => setAttempt((a) => a + 1)}
            />
          </Stack>
        </div>
      ) : documents.length === 0 ? (
        <div className="dj-files-body">
          <Text type="label" weight="semibold">
            No documents yet
          </Text>
          <Text type="supporting" color="secondary">
            The corpus snapshot of {shortOid(manifest?.commit ?? '')} holds no `.md` document.
            Documents land with pushes; this page only reads.
          </Text>
        </div>
      ) : (
        <div className="dj-files-split">
          <nav className="dj-files-tree" aria-label="Documents">
            {tree.dirs.map((dir) => renderDir(dir))}
            {tree.docs.map((docNode) => renderDoc(docNode))}
          </nav>

          <section className="dj-files-reader" aria-label="Document">
            {doc.state === 'idle' ? (
              <div className="dj-files-body">
                <Text type="supporting" color="secondary">
                  Select a document to read it. Sidecars and local mirrors are not listed — they
                  are data, not reading; their rows render on the dashboards.
                </Text>
              </div>
            ) : doc.state === 'reading' ? (
              <div className="dj-files-body" aria-hidden="true">
                <Skeleton width="30%" height="16px" index={0} />
                <Skeleton width="90%" height="12px" index={1} />
                <Skeleton width="84%" height="12px" index={2} />
              </div>
            ) : doc.state === 'refused' ? (
              <div className="dj-files-body">
                <Stack direction="horizontal" gap={2} vAlign="center">
                  <StatusDot variant="error" label="Document refused" />
                  <Text type="label" weight="semibold">
                    {doc.path}
                  </Text>
                </Stack>
                <Text type="code" color="secondary">
                  {doc.reason}
                </Text>
              </div>
            ) : doc.state === 'toolarge' ? (
              <div className="dj-files-body">
                <Text type="label" weight="semibold">
                  {doc.path}
                </Text>
                <Text type="supporting" color="secondary">
                  This document is {humanBytes(doc.size)} — past the {humanBytes(RENDER_CAP_BYTES)}{' '}
                  reading cap, so it is not fetched. It stays exactly readable in the repository.
                </Text>
              </div>
            ) : (
              <article className="dj-files-doc-view">
                <Text type="code" color="secondary">
                  {doc.path}
                </Text>
                {doc.frontmatter.length > 0 ? (
                  <div className="dj-files-frontmatter">
                    <Eyebrow>Frontmatter</Eyebrow>
                    <pre>{doc.frontmatter.join('\n')}</pre>
                  </div>
                ) : null}
                {/* the renderer escapes every author byte before emitting a
                    tag (web/src/lib/markdown.ts, SAFETY note) — this sink
                    only ever receives that module's output */}
                <div
                  className="dj-files-markdown"
                  onClick={onBodyClick}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: doc.html }}
                />
              </article>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
